import React, { useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import isEqual from 'lodash/isEqual';
import {
  ActionIcon,
  ColumnIcon,
  defaultStrings,
  FilterIcon,
  MobileGridWrapper,
  MobileGridFooter,
  ScrollToTop,
  SortIcon
} from '@glu/grid-mobile-react';
import gluLocale from '@glu/locale';
import { Drawer } from '@glu/drawer-react';
import { updateGridView } from 'logic/store/grids/actions';
import isEmpty from 'lodash/isEmpty';
import LocaleContext from '../locale/LocaleContext';
import useMobileGrid from './useMobileGrid';
import { formatColumnDefs, formatRowActionDefs, formatData } from './BasicMobileGrid.utils';
import ColumnPanel from './components/ColumnPanel';
import FilterPanel from './components/FilterPanel';
import SortPanel from './components/SortPanel';
import reducer from './logic/reducer';
import MobileGridContext from './logic/MobileGridContext';
import getFilteredRows from './logic/getFilteredRows';
import * as actions from './logic/actions';
import MobileGrid from './components/MobileGrid';
import BasicMobileGridSavedViews from './BasicMobileGridSavedViews';

// NOTE: This grid and entailing components are designed for UI filtering.
// Server-side pagination with filters would require a number of modifications and should be created as a separate ServerSideMobileGrid component or something
export function BasicMobileGridComponent(props) {
  gluLocale.set(defaultStrings);
  const locale = useContext(LocaleContext);
  const {
    columnConditions,
    columnDefs,
    getRowActions,
    gridId,
    id,
    pager,
    records,
    rowActions,
    savedViews
  } = props;
  const formattedActionRowDefs = formatRowActionDefs(
    !isEmpty(getRowActions) ? getRowActions : rowActions
  );
  const reduxDispatch = useDispatch();
  const activeGridView = useSelector((state) => state.grids[gridId] || {});
  const {
    activeViewId,
    sortState: activeSortState = {},
    filterState: activeFilterState = [],
    columnState: activeColumnState = []
  } = activeGridView;

  const formattedData = formatData(records, formattedActionRowDefs, columnDefs, locale);
  const paginatedData = formattedData.slice(0, pager.pageSize);
  const formattedColumnDefs = formatColumnDefs(columnDefs, locale);
  // useMobileGrid ColumnsPanel wants primary columns in the first part of the array
  const sortedColumnState = [
    ...formattedColumnDefs.filter((column) => column.primary === true),
    ...formattedColumnDefs.filter((column) => !column.primary)
  ];

  const defaultSavedViewId = savedViews?.views.find(
    ({ viewData }) => viewData && viewData?.isDefault
  )?.viewData?.id;

  const gridContext = useMobileGrid({
    gridReducer: reducer,
    initialState: {
      bulkActionDefs: [],
      bulkActionState: false,
      columnState: activeColumnState.length > 0 ? activeColumnState : sortedColumnState,
      columnsWithoutActions: activeColumnState.length > 0 ? activeColumnState : sortedColumnState,
      data: paginatedData,
      dataChanging: false,
      defaultSavedView: undefined,
      drawerOpen: false,
      enableSavedViews: false,
      filterState: activeFilterState,
      gridId,
      isPaginating: false,
      loading: false,
      pageSize: pager.pageSize,
      page: 1,
      rowActionDefs: formattedActionRowDefs,
      savedViewState: {
        active: activeViewId || savedViews.active || defaultSavedViewId,
        views: savedViews?.views || []
      },
      selectedRows: [],
      sortState: activeSortState,
      totalData: formattedData,
      totalRows: formattedData?.length,
      unAppliedFilterState: []
    }
  });
  // NOTE: Rename setGridState to dispatch since that is actually what it is
  gridContext.dispatch = gridContext.setGridState;

  // If records prop changes we need to update gridContext
  React.useEffect(() => {
    if (!isEqual(formattedData, gridContext.totalData)) {
      const { paginatedData, totalRows } = getFilteredRows({
        columnState: gridContext.columnState,
        filterState: gridContext.filterState,
        sortState: gridContext.sortState,
        formattedColumnDefs,
        page: gridContext.page,
        pageSize: gridContext.pageSize,
        rows: formattedData
      });
      gridContext.dispatch({
        data: paginatedData,
        totalData: formattedData,
        totalRows
      });
    }
  }, [formattedColumnDefs, formattedData, paginatedData, gridContext, gridContext.totalData]);

  const onSaveSort = (sortState) => {
    const { paginatedData, totalRows } = getFilteredRows({
      columnState: gridContext.columnState,
      filterState: gridContext.filterState,
      formattedColumnDefs,
      sortState,
      page: gridContext.page,
      pageSize: gridContext.pageSize,
      rows: gridContext.totalData
    });
    actions.saveSort({
      gridContext,
      payload: {
        sortState,
        data: paginatedData,
        totalRows
      }
    });
    // Save grid state between page navigation
    updateGridView({ gridId, sortState, dispatch: reduxDispatch });
  };

  // When Load More Button is Tapped
  const pageGridData = useCallback(() => {
    // This would be the api call used to get next set of data
    actions.loadMore.request({ formattedColumnDefs, gridContext, records });
  }, [formattedColumnDefs, gridContext, records]);

  gridContext.columnConditions = columnConditions;

  return (
    <MobileGridContext.Provider value={gridContext}>
      <MobileGridWrapper>
        <BasicMobileGridSavedViews
          formattedColumnDefs={formattedColumnDefs}
          gridId={gridId}
          id={id}
          savedViews={savedViews}
        />
        <MobileGrid pageData={pageGridData}>{gridContext.gridContent}</MobileGrid>
        <ScrollToTop />
        <MobileGridFooter>
          {/* NOTE: Future Update will have bulk actions on Mobile Grid I bet you money */}
          {gridContext.bulkActionState && (
            <ActionIcon
              actionControlDefs={gridContext.bulkActionDefs}
              bulkActionState={gridContext.bulkActionState}
              setBulkActionState={(newState) => {
                // TODO: Turn into action
                return gridContext.dispatch({ bulkActionState: newState });
              }}
              selectedRows={gridContext.selectedRows}
              data={gridContext.data}
            />
          )}
          <SortIcon onClick={() => gridContext.openDrawer('sort')} />
          <ColumnIcon onClick={() => gridContext.openDrawer('column')} />
          <FilterIcon onClick={() => gridContext.openDrawer('filter')} />
        </MobileGridFooter>
        {!gridContext.loading ? (
          <Drawer open={gridContext.drawerOpen} anchorTo="right" size="100%">
            {gridContext.drawer === 'sort' && (
              <SortPanel
                closeDrawer={() => gridContext.closeDrawer()}
                onSave={onSaveSort}
                sortFieldId={gridContext.sortState.field}
                sortDirection={gridContext.sortState.direction}
              />
            )}
            {gridContext.drawer === 'filter' && (
              <FilterPanel formattedColumnDefs={formattedColumnDefs} />
            )}
            {gridContext.drawer === 'column' && <ColumnPanel />}
          </Drawer>
        ) : null}
      </MobileGridWrapper>
    </MobileGridContext.Provider>
  );
}

BasicMobileGridComponent.propTypes = {
  columnDefs: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  columnConditions: PropTypes.shape({}),
  gridId: PropTypes.string,
  id: PropTypes.string,
  localeText: PropTypes.shape({
    // TODO: Handle custom messages when no rows displaying
    noRowsToShow: PropTypes.string
  }),
  pager: PropTypes.shape({
    startAt: PropTypes.number,
    size: PropTypes.number
  }),
  records: PropTypes.arrayOf(PropTypes.shape({})),
  rowActions: PropTypes.arrayOf(PropTypes.object),
  getRowActions: PropTypes.arrayOf(PropTypes.object),
  savedViews: PropTypes.shape({
    views: PropTypes.arrayOf(PropTypes.shape({})),
    active: PropTypes.number
  }),
  // `rowIdColumn` needs to be REAL property from data object,
  formatters: PropTypes.shape({
    valueFallback: PropTypes.string,
    fields: PropTypes.arrayOf(PropTypes.shape({}))
  })
};

BasicMobileGridComponent.defaultProps = {
  columnConditions: {},
  gridId: 'basicMobileGridId',
  id: 'basicMobileGridId',
  pager: {
    startAt: 0,
    pageSize: 10
  },
  records: [],
  localeText: null,
  getRowActions: null,
  savedViews: null,
  rowActions: [],
  formatters: {
    valueFallback: '',
    fields: []
  }
};

export default BasicMobileGridComponent;
